import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Icon } from '@iconify/react';

import Navbar from '../components/Layout/Navbar';
import Footer from '../components/Layout/Footer';
import { getBlogPost } from '../utils/api';

import './Blog.css';
import './BlogPost.css';

const formatDate = (d) =>
  new Date(d).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

/* =========================================================
   INLINE TEXT FORMATTER
   Supports:
   **bold**
   *italic*
   [link text](url)
========================================================= */

const renderInlineText = (text) => {
  if (!text) return null;

  const parts = [];
  let remaining = text;
  let key = 0;

  const regex =
    /(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/;

  while (remaining.length > 0) {
    const match = remaining.match(regex);

    if (!match) {
      parts.push(remaining);
      break;
    }

    const index = match.index;

    if (index > 0) {
      parts.push(remaining.slice(0, index));
    }

    const value = match[0];

    /* Bold */
    if (value.startsWith('**') && value.endsWith('**')) {
      parts.push(
        <strong key={key++}>
          {value.slice(2, -2)}
        </strong>
      );
    }

    /* Italic */
    else if (value.startsWith('*') && value.endsWith('*')) {
      parts.push(
        <em key={key++}>
          {value.slice(1, -1)}
        </em>
      );
    }

    /* Link */
    else if (value.startsWith('[')) {
      const linkMatch = value.match(/^\[([^\]]+)\]\(([^)]+)\)$/);

      if (linkMatch) {
        parts.push(
          <a
            key={key++}
            href={linkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
          >
            {linkMatch[1]}
          </a>
        );
      } else {
        parts.push(value);
      }
    }

    remaining = remaining.slice(index + value.length);
  }

  return parts;
};

/* =========================================================
   CONTENT PARSER

   Supports:

   # Main Heading
   ## Section Heading
   ### Sub Heading

   - Bullet
   - Bullet

   1. Numbered item
   2. Numbered item

   > Quote

   Normal paragraph
========================================================= */

const renderContent = (content) => {
  if (!content) return null;

  const lines = content.replace(/\r\n/g, '\n').split('\n');

  const elements = [];

  let paragraphLines = [];
  let bulletItems = [];
  let numberedItems = [];

  const flushParagraph = () => {
    if (!paragraphLines.length) return;

    const text = paragraphLines.join(' ').trim();

    if (text) {
      elements.push(
        <p key={`paragraph-${elements.length}`}>
          {renderInlineText(text)}
        </p>
      );
    }

    paragraphLines = [];
  };

  /* =========================================================
     BULLET LIST

     Short bullet lists:
     HTML • CSS • JavaScript • React.js

     Long bullet lists:
     Normal vertical bullet list
  ========================================================= */

  const flushBulletList = () => {
    if (!bulletItems.length) return;

    /*
      If every bullet item is short, display them
      horizontally using the short-bullet-list class.
    */
    const isShortList = bulletItems.every(
      (item) => item.length <= 35
    );

    elements.push(
      <ul
        key={`ul-${elements.length}`}
        className={
          isShortList
            ? 'short-bullet-list'
            : 'normal-bullet-list'
        }
      >
        {bulletItems.map((item, index) => (
          <li key={index}>
            {renderInlineText(item)}
          </li>
        ))}
      </ul>
    );

    bulletItems = [];
  };

  const flushNumberedList = () => {
    if (!numberedItems.length) return;

    elements.push(
      <ol key={`ol-${elements.length}`}>
        {numberedItems.map((item, index) => (
          <li key={index}>
            {renderInlineText(item)}
          </li>
        ))}
      </ol>
    );

    numberedItems = [];
  };

  const flushAll = () => {
    flushParagraph();
    flushBulletList();
    flushNumberedList();
  };

  lines.forEach((rawLine, index) => {
    const line = rawLine.trim();

    /* Blank line */
    if (!line) {
      flushAll();
      return;
    }

    /* =====================================================
       H1
    ===================================================== */

    if (line.startsWith('# ') && !line.startsWith('## ')) {
      flushAll();

      elements.push(
        <h2 key={`h1-${index}`}>
          {renderInlineText(line.slice(2).trim())}
        </h2>
      );

      return;
    }

    /* =====================================================
       H2
    ===================================================== */

    if (line.startsWith('## ') && !line.startsWith('### ')) {
      flushAll();

      elements.push(
        <h2 key={`h2-${index}`}>
          {renderInlineText(line.slice(3).trim())}
        </h2>
      );

      return;
    }

    /* =====================================================
       H3
    ===================================================== */

    if (line.startsWith('### ')) {
      flushAll();

      elements.push(
        <h3 key={`h3-${index}`}>
          {renderInlineText(line.slice(4).trim())}
        </h3>
      );

      return;
    }

    /* =====================================================
       H4
    ===================================================== */

    if (line.startsWith('#### ')) {
      flushAll();

      elements.push(
        <h4 key={`h4-${index}`}>
          {renderInlineText(line.slice(5).trim())}
        </h4>
      );

      return;
    }

    /* =====================================================
       BULLET LIST
    ===================================================== */

    if (/^[-*]\s+/.test(line)) {
      flushParagraph();
      flushNumberedList();

      bulletItems.push(
        line.replace(/^[-*]\s+/, '').trim()
      );

      return;
    }

    /* =====================================================
       NUMBERED LIST
    ===================================================== */

    if (/^\d+\.\s+/.test(line)) {
      flushParagraph();
      flushBulletList();

      numberedItems.push(
        line.replace(/^\d+\.\s+/, '').trim()
      );

      return;
    }

    /* =====================================================
       BLOCKQUOTE
    ===================================================== */

    if (line.startsWith('> ')) {
      flushAll();

      elements.push(
        <blockquote key={`quote-${index}`}>
          <p>
            {renderInlineText(line.slice(2).trim())}
          </p>
        </blockquote>
      );

      return;
    }

    /* =====================================================
       NORMAL PARAGRAPH
    ===================================================== */

    paragraphLines.push(line);
  });

  flushAll();

  return elements;
};

const BlogPost = () => {
  const { slug } = useParams();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);

    (async () => {
      try {
        const res = await getBlogPost(slug);

        setPost(res.data?.data || null);
      } catch (err) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  /* =========================================================
     LOADING
  ========================================================= */

  if (loading) {
    return (
      <div className="blog-page">
        <Navbar />

        <div
          className="blog-empty"
          style={{ minHeight: '50vh' }}
        >
          <Icon
            icon="mdi:loading"
            width={28}
            height={28}
            className="blog-spin"
          />

          <p>Loading…</p>
        </div>
      </div>
    );
  }

  /* =========================================================
     NOT FOUND
  ========================================================= */

  if (notFound || !post) {
    return (
      <div className="blog-page">
        <Navbar />

        <div
          className="blog-empty"
          style={{ minHeight: '50vh' }}
        >
          <Icon
            icon="mdi:file-remove-outline"
            width={28}
            height={28}
          />

          <p>Post not found.</p>

          <Link
            to="/blog"
            className="blog-back-link"
          >
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  /* =========================================================
     BLOG POST
  ========================================================= */

  return (
    <div className="blog-page">
      <Navbar />

      <article className="blog-post-detail">
        <div className="blog-post-detail-container">

          {/* Back */}
          <Link
            to="/blog"
            className="blog-back-link"
          >
            <Icon
              icon="mdi:arrow-left"
              width={16}
              height={16}
            />

            Back to Blog
          </Link>

          {/* =================================================
              HERO
          ================================================= */}

          <div className="blog-post-hero">

            {/* Cover Image */}
            <div className="blog-post-hero-image">
              {post.coverImageUrl ? (
                <img
                  src={post.coverImageUrl}
                  alt={post.title}
                />
              ) : (
                <div className="blog-post-hero-image-fallback">
                  <Icon
                    icon="mdi:image-outline"
                    width={48}
                    height={48}
                  />
                </div>
              )}
            </div>

            {/* Post Info */}
            <div className="blog-post-hero-content">

              {/* Tags */}
              {!!post.tags?.length && (
                <div className="blog-card-tags">
                  {post.tags.map((tag) => (
                    <span key={tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Title */}
              <h1 className="blog-post-title">
                {post.title}
              </h1>

              {/* Excerpt */}
              {post.excerpt && (
                <p className="blog-post-excerpt">
                  {post.excerpt}
                </p>
              )}

              {/* Meta */}
              <div className="blog-card-meta">

                <Icon
                  icon="mdi:account-circle"
                  width={18}
                  height={18}
                />

                <span>
                  {post.author?.name || 'WeIntern Team'}
                </span>

                <span>·</span>

                <Icon
                  icon="mdi:calendar"
                  width={16}
                  height={16}
                />

                <span>
                  {formatDate(post.createdAt)}
                </span>

              </div>

            </div>
          </div>

          {/* =================================================
              ARTICLE CONTENT
          ================================================= */}

          <div className="blog-post-content">
            {renderContent(post.content)}
          </div>

        </div>
      </article>
      <Footer/>
    </div>
  );
};

export default BlogPost;